"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SwapGateway = void 0;
const tslib_1 = require("tslib");
const cetus_js_1 = require("./cetus.js");
const constants_js_1 = require("./cetus/constants.js");
const hop_js_1 = require("./hop.js");
const bn_js_1 = tslib_1.__importDefault(require("bn.js"));
const _7k_js_1 = require("./7k.js");
const index_js_1 = require("./pyth/index.js");
class SwapGateway {
    constructor() {
        this.hopGateway = new hop_js_1.HopGateway(hop_js_1.hopSDKOptions);
        this.cetusGateway = new cetus_js_1.CetusGateway(constants_js_1.cetusMainnetSDKOptions);
        this.sevenKGateway = new _7k_js_1.SevenKGateway();
        // this.aftermathGateway = new AftermathGateway();
    }
    async getQuote(swapOptions, debug = false) {
        this.swapOptions = swapOptions;
        // Parallelize the calls to the two gateways using Promise.all
        if (debug)
            console.time("cetusQuote");
        const cetusQuotePromise = this.cetusGateway
            .getPrice(this.swapOptions)
            .finally(() => {
            if (debug)
                console.timeEnd("cetusQuote");
        });
        if (debug)
            console.time("hopQuote");
        const hopQuotePromise = this.hopGateway
            .getQuote(this.swapOptions)
            .finally(() => {
            if (debug)
                console.timeEnd("hopQuote");
        });
        const [cetusQuote, hopQuote] = await Promise.all([
            cetusQuotePromise,
            hopQuotePromise,
        ]);
        this.cetusQuote = cetusQuote;
        this.hopQuote = hopQuote;
        if (!this.cetusQuote || !this.hopQuote) {
            return;
        }
        const cetusEstimatedAmountOut = this.cetusQuote.estimatedAmountOut;
        const cetusEstimatedFeeAmount = this.cetusQuote.estimatedFeeAmount;
        //------------ debug ----------------------
        if (debug) {
            console.debug("From CETUS:");
            console.debug("calculateRates###res###", {
                estimatedAmountIn: this.cetusQuote.estimatedAmountIn.toString(),
                estimatedAmountOut: this.cetusQuote.estimatedAmountOut.toString(),
                estimatedEndSqrtPrice: this.cetusQuote.estimatedEndSqrtPrice.toString(),
                estimatedFeeAmount: this.cetusQuote.estimatedFeeAmount.toString(),
                isExceed: this.cetusQuote.isExceed,
                extraComputeLimit: this.cetusQuote.extraComputeLimit,
                amount: this.cetusQuote.amount.toString(),
                aToB: this.cetusQuote.aToB,
                byAmountIn: this.cetusQuote.byAmountIn,
            });
        }
        // ----------------------------------------
        const hopEstimatedAmountOut = new bn_js_1.default(this.hopQuote.trade.amount_out.amount.toString());
        const hopEstimatedAmountOutWithFee = new bn_js_1.default(this.hopQuote.amount_out_with_fee.toString());
        const hopEstimatedFeeAmount = hopEstimatedAmountOutWithFee.sub(new bn_js_1.default(hopEstimatedAmountOut));
        // ------------- debug ------------------
        if (debug) {
            console.debug("From HOP:");
            console.debug(this.hopQuote);
        }
        // --------------------------------------
        let amount = new bn_js_1.default(0);
        if (this.swapOptions.inAmount) {
            amount = this.swapOptions.inAmount;
        }
        else if (this.swapOptions.outAmount) {
            amount = this.swapOptions.outAmount;
        }
        const pairNameA = (this.swapOptions.pair.coinA.name +
            "/" +
            "USD");
        const pairNameB = (this.swapOptions.pair.coinB.name +
            "/" +
            "USD");
        const [priceA, priceB] = await (0, index_js_1.getLatestPrices)([pairNameA, pairNameB], true);
        if (priceA && priceB) {
            if (cetusEstimatedAmountOut.gt(hopEstimatedAmountOut)) {
                const inputAmountInUSD = amount.toNumber() *
                    Math.pow(10, this.swapOptions.pair.coinA.expo) *
                    parseFloat(priceA);
                const outputAmountInUSD = cetusEstimatedAmountOut.toNumber() *
                    Math.pow(10, this.swapOptions.pair.coinB.expo) *
                    parseFloat(priceB);
                const slippage = (outputAmountInUSD - inputAmountInUSD) / inputAmountInUSD;
                this.bestQuote = {
                    gateway: "cetus",
                    estimatedAmountOut: cetusEstimatedAmountOut,
                    estimatedFeeAmount: cetusEstimatedFeeAmount,
                    inputAmount: amount,
                    inputAmountInUSD: inputAmountInUSD,
                    estimatedAmountOutInUSD: outputAmountInUSD,
                    slippage: slippage,
                };
            }
            else {
                const inputAmountInUSD = amount.toNumber() *
                    Math.pow(10, this.swapOptions.pair.coinA.expo) *
                    parseFloat(priceA);
                const outputAmountInUSD = hopEstimatedAmountOut.toNumber() *
                    Math.pow(10, this.swapOptions.pair.coinB.expo) *
                    parseFloat(priceB);
                const slippage = (outputAmountInUSD - inputAmountInUSD) / inputAmountInUSD;
                this.bestQuote = {
                    gateway: "hop",
                    estimatedAmountOut: hopEstimatedAmountOut,
                    estimatedFeeAmount: hopEstimatedFeeAmount,
                    inputAmount: amount,
                    inputAmountInUSD: inputAmountInUSD,
                    estimatedAmountOutInUSD: outputAmountInUSD,
                    slippage: slippage,
                };
            }
        }
        else {
            console.error("Could not get prices from Pyth Network.");
        }
        return this.bestQuote;
    }
    async getHopQuote(swapOptions, debug = false) {
        this.swapOptions = swapOptions;
        if (debug)
            console.time("hopQuote");
        const hopQuotePromise = this.hopGateway
            .getQuote(this.swapOptions, debug)
            .finally(() => {
            if (debug)
                console.timeEnd("hopQuote");
        });
        this.hopQuote = await hopQuotePromise;
        if (!this.hopQuote)
            return;
        const hopEstimatedAmountOut = new bn_js_1.default(this.hopQuote.trade.amount_out.amount.toString());
        const hopEstimatedAmountOutWithFee = new bn_js_1.default(this.hopQuote.amount_out_with_fee.toString());
        const hopEstimatedFeeAmount = hopEstimatedAmountOutWithFee.sub(new bn_js_1.default(hopEstimatedAmountOut));
        if (debug) {
            console.debug("From HOP:");
            console.debug(this.hopQuote);
        }
        let amount = new bn_js_1.default(0);
        if (this.swapOptions.inAmount) {
            amount = this.swapOptions.inAmount;
        }
        else if (this.swapOptions.outAmount) {
            amount = this.swapOptions.outAmount;
        }
        const pairNameA = (this.swapOptions.pair.coinA.name +
            "/" +
            "USD");
        const pairNameB = (this.swapOptions.pair.coinB.name +
            "/" +
            "USD");
        const [priceA, priceB] = await (0, index_js_1.getLatestPrices)([pairNameA, pairNameB], true);
        if (priceA && priceB) {
            if (debug) {
                console.debug("token a price", priceA, "token b price", priceB);
            }
            const inputAmountInUSD = (amount.toNumber() / Math.pow(10, this.swapOptions.pair.coinA.expo)) *
                parseFloat(priceA);
            const outputAmountInUSD = (hopEstimatedAmountOut.toNumber() /
                Math.pow(10, this.swapOptions.pair.coinB.expo)) *
                parseFloat(priceB);
            const slippage = (inputAmountInUSD - outputAmountInUSD) / inputAmountInUSD;
            this.bestQuote = {
                gateway: "hop",
                estimatedAmountOut: hopEstimatedAmountOut,
                estimatedFeeAmount: hopEstimatedFeeAmount,
                inputAmount: amount,
                inputAmountInUSD: inputAmountInUSD,
                estimatedAmountOutInUSD: outputAmountInUSD,
                slippage: slippage,
            };
        }
        else {
            console.error("Could not get prices from Pyth Network.");
        }
        return this.bestQuote;
    }
    async getSevenKQuote(swapOptions, debug = false) {
        this.swapOptions = swapOptions;
        if (debug)
            console.time("sevenKQuote");
        const sevenKQuotePromise = this.sevenKGateway
            .getQuote(this.swapOptions)
            .finally(() => {
            if (debug)
                console.timeEnd("sevenKQuote");
        });
        this.sevenKQuote = await sevenKQuotePromise;
        const sevenKEstimatedAmountOut = new bn_js_1.default(this.sevenKQuote
            ? this.sevenKQuote.returnAmountWithDecimal.toString()
            : 0);
        const sevenKEstimatedAmountOutWithoutFee = new bn_js_1.default(this.sevenKQuote
            ? this.sevenKQuote.returnAmountWithoutSwapFees
                ? this.sevenKQuote.returnAmountWithoutSwapFees.toString()
                : sevenKEstimatedAmountOut.toString()
            : sevenKEstimatedAmountOut.toString());
        const sevenKEstimatedFeeAmount = sevenKEstimatedAmountOut.sub(new bn_js_1.default(sevenKEstimatedAmountOutWithoutFee));
        if (debug) {
            console.debug("From 7k:");
            console.debug(this.sevenKQuote);
        }
        const amount = new bn_js_1.default(this.sevenKQuote ? this.sevenKQuote.swapAmountWithDecimal : 0);
        const pairNameA = (this.swapOptions.pair.coinA.name +
            "/" +
            "USD");
        const pairNameB = (this.swapOptions.pair.coinB.name +
            "/" +
            "USD");
        const [priceA, priceB] = await (0, index_js_1.getLatestPrices)([pairNameA, pairNameB], true);
        if (priceA && priceB) {
            if (debug) {
                console.debug("token a price", priceA, "token b price", priceB);
            }
            const inputAmountInUSD = (amount.toNumber() / Math.pow(10, this.swapOptions.pair.coinA.expo)) *
                parseFloat(priceA);
            const outputAmountInUSD = (sevenKEstimatedAmountOut.toNumber() /
                Math.pow(10, this.swapOptions.pair.coinB.expo)) *
                parseFloat(priceB);
            const slippage = (inputAmountInUSD - outputAmountInUSD) / inputAmountInUSD;
            this.bestQuote = {
                gateway: "7k",
                estimatedAmountOut: sevenKEstimatedAmountOut,
                estimatedFeeAmount: sevenKEstimatedFeeAmount,
                inputAmount: amount,
                inputAmountInUSD: inputAmountInUSD,
                estimatedAmountOutInUSD: outputAmountInUSD,
                slippage: slippage,
            };
        }
        else {
            console.error("Could not get prices from Pyth Network.");
        }
        return this.bestQuote;
    }
    // async getAftermathQuote(
    //   swapOptions: SwapOptions,
    //   debug: boolean = false,
    // ): Promise<SwapQuote | undefined> {
    //   this.swapOptions = swapOptions;
    //   if (debug) console.time("aftermathQuote");
    //   const aftermathQuotePromise = this.aftermathGateway
    //     .getQuote(this.swapOptions)
    //     .finally(() => {
    //       if (debug) console.timeEnd("aftermathQuote");
    //     });
    //   this.aftermathQuote = await aftermathQuotePromise;
    //   const aftermathEstimatedAmountOut = new BN(
    //     this.aftermathQuote ? this.aftermathQuote.coinOut.amount.toString() : 0,
    //   );
    //   const aftermathEstimatedFeeAmount = (
    //     parseFloat(aftermathEstimatedAmountOut.toString()) *
    //     parseFloat(
    //       this.aftermathQuote
    //         ? this.aftermathQuote.netTradeFeePercentage.toString()
    //         : "0",
    //     )
    //   ).toFixed(0);
    //   if (debug) {
    //     console.debug("From aftermath:");
    //     console.debug(this.aftermathQuote);
    //   }
    //   const amount: BN = new BN(
    //     this.aftermathQuote ? this.aftermathQuote.coinIn.amount.toString() : 0,
    //   );
    //   const pairNameA: PythPriceIdPair = (this.swapOptions.pair.coinA.name +
    //     "/" +
    //     "USD") as PythPriceIdPair;
    //   const pairNameB: PythPriceIdPair = (this.swapOptions.pair.coinB.name +
    //     "/" +
    //     "USD") as PythPriceIdPair;
    //   const [priceA, priceB] = await getLatestPrices(
    //     [pairNameA, pairNameB],
    //     true,
    //   );
    //   if (priceA && priceB) {
    //     if (debug) {
    //       console.debug("token a price", priceA, "token b price", priceB);
    //     }
    //     const inputAmountInUSD =
    //       (amount.toNumber() / Math.pow(10, this.swapOptions.pair.coinA.expo)) *
    //       parseFloat(priceA);
    //     const outputAmountInUSD =
    //       (aftermathEstimatedAmountOut.toNumber() /
    //         Math.pow(10, this.swapOptions.pair.coinB.expo)) *
    //       parseFloat(priceB);
    //     const slippage =
    //       (inputAmountInUSD - outputAmountInUSD) / inputAmountInUSD;
    //     this.bestQuote = {
    //       gateway: "aftermath",
    //       estimatedAmountOut: aftermathEstimatedAmountOut,
    //       estimatedFeeAmount: new BN(aftermathEstimatedFeeAmount),
    //       inputAmount: amount,
    //       inputAmountInUSD: inputAmountInUSD,
    //       estimatedAmountOutInUSD: outputAmountInUSD,
    //       slippage: slippage,
    //     };
    //   } else {
    //     console.error("Could not get prices from Pyth Network.");
    //   }
    //   return this.bestQuote;
    // }
    async getBestQuoteFromAll(swapOptions, debug = false) {
        this.swapOptions = swapOptions;
        const [sevenKQuote] = await Promise.all([
            // this.getHopQuote(swapOptions, debug),
            this.getSevenKQuote(swapOptions, debug),
            // this.getAftermathQuote(swapOptions, debug),
        ]);
        // if (!sevenKQuote && !aftermathQuote && !hopQuote) {
        //   console.error(`Error fetching swap quote`);
        // }
        const bestQuote = sevenKQuote;
        // let bestQuote: SwapQuote = (hopQuote ??
        //   sevenKQuote ??
        //   aftermathQuote) as SwapQuote;
        // if (
        //   sevenKQuote &&
        //   sevenKQuote.estimatedAmountOutInUSD > bestQuote.estimatedAmountOutInUSD
        // ) {
        //   bestQuote = sevenKQuote;
        // }
        // if (
        //   aftermathQuote &&
        //   aftermathQuote.estimatedAmountOutInUSD > bestQuote.estimatedAmountOutInUSD
        // ) {
        //   bestQuote = aftermathQuote;
        // }
        this.bestQuote = bestQuote;
        return bestQuote;
    }
    async getTransactionBlock(debug = false, transaction = undefined) {
        if (this.bestQuote &&
            this.bestQuote.gateway === "hop" &&
            this.hopQuote &&
            this.swapOptions) {
            const txb = await this.hopGateway.getTransactionBlock(this.swapOptions, this.hopQuote, debug, transaction);
            return txb;
        }
        else if (this.bestQuote &&
            this.bestQuote.gateway === "cetus" &&
            this.cetusQuote &&
            this.swapOptions) {
            const txb = await this.cetusGateway.getTransaction(this.swapOptions);
            return txb;
        }
        else if (this.bestQuote &&
            this.bestQuote.gateway === "7k" &&
            this.sevenKQuote &&
            this.swapOptions) {
            const { tx, coinOut } = await this.sevenKGateway.getTransactionBlock(this.swapOptions, this.sevenKQuote, transaction);
            if (coinOut)
                tx.transferObjects([coinOut], this.swapOptions.senderAddress);
            return tx;
        }
        // else if (
        //   this.bestQuote &&
        //   this.bestQuote.gateway === "aftermath" &&
        //   this.aftermathQuote &&
        //   this.swapOptions
        // ) {
        //   const tx = await this.aftermathGateway.getTransactionBlock(
        //     this.swapOptions,
        //     this.aftermathQuote,
        //   );
        //   return tx;
        // }
    }
    // Debug methods
    bnToString(bn) {
        return bn.toString(10); // Convert BN to string in base 10
    }
    swapQuoteToString(swapQuote) {
        return `
SwapQuote:
  Gateway: ${swapQuote.gateway}
  Estimated Amount Out: ${this.bnToString(swapQuote.estimatedAmountOut)}
  Estimated Fee Amount: ${this.bnToString(swapQuote.estimatedFeeAmount)}
  Input Amount: ${this.bnToString(swapQuote.inputAmount)}
  Input Amount in USD: ${swapQuote.inputAmountInUSD.toFixed(2)}
  Estimated Amount Out in USD: ${swapQuote.estimatedAmountOutInUSD.toFixed(2)}
  Slippage: ${swapQuote.slippage.toFixed(2)}
  `;
    }
}
exports.SwapGateway = SwapGateway;
//# sourceMappingURL=swap.js.map